import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, ArrowRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SymptomSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef(null);

  // Load popular searches on mount
  useEffect(() => {
    axios.get(`${API}/search/symptoms?q=`).then(r => setPopular(r.data.popular || [])).catch(() => {});
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setResults([]); setSearched(false); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('biounica_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await axios.get(`${API}/search/symptoms?q=${encodeURIComponent(q.trim())}`, { headers });
      setResults(resp.data.results || []);
      setSearched(true);
    } catch { setResults([]); }
    setLoading(false);
  }, []);

  const handleChange = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleChipClick = (term) => {
    setQuery(term);
    doSearch(term);
  };

  return (
    <div data-testid="symptom-search">
      {/* Search Input */}
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardContent className="p-5 sm:p-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4a4238]" />
            <Input
              value={query}
              onChange={(e) => handleChange(e.target.value)}
              placeholder="Digita un sintomo, una condizione o una parola chiave... es. 'gonfiore', 'stanchezza cronica', 'candida'"
              className="pl-12 pr-4 py-6 rounded-xl border-gray-300 focus:border-[#c6a15b] focus:ring-[#c6a15b] text-base"
              data-testid="symptom-search-input"
            />
            {loading && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#4a4238]" />}
          </div>

          {/* Popular searches */}
          {!searched && popular.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium text-[#4a4238] mb-2">Ricerche frequenti:</p>
              <div className="flex flex-wrap gap-2">
                {popular.map(term => (
                  <button
                    key={term}
                    onClick={() => handleChipClick(term)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#12100c]/5 text-[#12100c] hover:bg-[#12100c]/10 transition-colors"
                    data-testid={`popular-chip-${term.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {searched && (
        <div className="mt-4 space-y-3" data-testid="search-results">
          {results.length > 0 ? (
            <>
              <p className="text-xs text-[#4a4238]">{results.length} area{results.length > 1 ? 'e' : ''} trovata{results.length > 1 ? 'e' : ''} per "{query}"</p>
              {results.map(area => (
                <Card key={area.slug} className="rounded-2xl border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`search-result-${area.slug}`}>
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-[#12100c]/5 flex items-center justify-center shrink-0 text-xl">
                        {area.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-sm text-[#12100c]">{area.title}</h3>
                          <Badge className={`text-[9px] border-none ${
                            area.priority === 'alta' ? 'bg-[#c6a15b]/15 text-[#8a6620]' : 'bg-[#5f7d52]/10 text-[#4a5f43]'
                          }`}>
                            {area.priority === 'alta' ? 'Alta priorità' : 'Media priorità'}
                          </Badge>
                        </div>
                        <p className="text-xs text-[#4a4238] leading-relaxed line-clamp-2">{area.mechanism_excerpt}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => navigate(`/aree-applicazione#${area.slug}`)}
                        className="bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full h-8 px-3 text-xs shrink-0"
                        data-testid={`search-go-${area.slug}`}
                      >
                        Approfondisci <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          ) : (
            <Card className="rounded-2xl border-gray-100 shadow-sm">
              <CardContent className="p-6 text-center" data-testid="search-no-results">
                <p className="text-sm text-[#4a4238] mb-2">Nessun risultato per "{query}".</p>
                <p className="text-xs text-[#4a4238]">Prova con parole chiave diverse oppure usa il <span className="font-medium text-[#4a5f43]">Wizard guidato</span> per una raccomandazione personalizzata.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
